import { Button, radius } from '@alison/ui';
import { useTenant } from '@alison/whitelabel/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { CadencePickerInline } from './CadencePickerInline.js';
import { ChannelPicker } from './ChannelPicker.js';
import { GLYPH_SX } from './channelCardShared.js';
import { allEventsFor, eventGroupsFor, ROLES } from './notifications.data.js';
import type {
  CadenceId,
  ChannelId,
  EmailData,
  EventPrefs,
  RoleId,
  SlackData,
} from './notifications.types.js';
import { OverrideChip } from './OverrideChip.js';
import { SlackIcon } from './SlackIcon.js';

const DRAWER_WIDTH = 760;

function FieldRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: 'flex-start',
        gap: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 0, pt: 0 },
        flexDirection: { xs: 'column', sm: 'row' },
      }}
    >
      <Typography
        sx={{
          width: 130,
          flexShrink: 0,
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'text.secondary',
          pt: 1,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Stack>
  );
}

interface ChannelManageDrawerProps {
  channelId: Exclude<ChannelId, 'inapp'> | null;
  slack: SlackData;
  email: EmailData;
  prefs: EventPrefs;
  roleId: RoleId;
  canManage: boolean;
  preferencesPending: boolean;
  testPending: boolean;
  disconnectPending: boolean;
  notice: { kind: 'error' | 'success'; text: string } | null;
  onToggle: (event: string, channel: Exclude<ChannelId, 'inapp'>) => void;
  onCadenceChange: (event: string, cadence: CadenceId) => void;
  onTest: () => void;
  onReconnect: () => void;
  onDisconnect: () => void;
  onClose: () => void;
}

export function ChannelManageDrawer({
  channelId,
  slack,
  email,
  prefs,
  roleId,
  canManage,
  preferencesPending,
  testPending,
  disconnectPending,
  notice,
  onToggle,
  onCadenceChange,
  onTest,
  onReconnect,
  onDisconnect,
  onClose,
}: ChannelManageDrawerProps): ReactNode {
  const { providerName } = useTenant();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const isSlack = channelId === 'slack';
  const eventGroups = eventGroupsFor(providerName);
  const allEvents = allEventsFor(providerName);
  const enabledCount = allEvents.filter(
    (event) => prefs[event.id as keyof EventPrefs][channelId ?? 'slack'],
  ).length;
  const roleLabel = ROLES.find((roleOption) => roleOption.id === roleId)?.label ?? 'All';

  return (
    <>
      <Drawer
        anchor="right"
        open={channelId !== null}
        onClose={onClose}
        slotProps={{
          backdrop: {
            sx: { bgcolor: 'rgba(15, 23, 43, 0.42)', backdropFilter: 'blur(4px)' },
          },
          paper: {
            'aria-labelledby': 'channel-manage-drawer-title',
            sx: {
              width: DRAWER_WIDTH,
              maxWidth: '96vw',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.page',
              boxShadow: '-20px 0 60px rgba(15, 23, 43, 0.25)',
            },
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            gap: 3,
            py: 4,
            px: 5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              ...GLYPH_SX,
              bgcolor: (theme) =>
                isSlack ? theme.palette.network.slack : theme.palette.primary.main,
            }}
          >
            {isSlack ? (
              <SlackIcon sx={{ fontSize: 24 }} />
            ) : (
              <EmailIcon sx={{ color: 'common.white', fontSize: 24 }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              id="channel-manage-drawer-title"
              component="h2"
              sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}
            >
              {isSlack ? 'Slack' : 'Email'} notification preferences
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
              {isSlack ? (
                <>
                  Workspace <strong>{slack.workspace}</strong> · default{' '}
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    #
                  </Box>
                  {slack.channel}
                </>
              ) : (
                <>
                  Sender <strong>{email.sender}</strong>
                </>
              )}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close drawer"
            sx={{
              width: 34,
              height: 34,
              borderRadius: `${radius.lg}px`,
              bgcolor: 'background.platform',
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <Stack
          sx={{
            flex: 1,
            overflowY: 'auto',
            py: 4,
            px: 5,
            gap: 4,
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${radius.lg}px`,
              boxShadow: 1,
              py: 3,
              px: 4,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 3 }}>
              {isSlack ? 'Delivery' : 'Recipients'}
            </Typography>

            {isSlack ? (
              <>
                <FieldRow label="Workspace">
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                    {slack.workspace}
                  </Typography>
                </FieldRow>
                <FieldRow label="Default channel">
                  <ChannelPicker current={slack.channel} onReconnect={onReconnect} />
                </FieldRow>
              </>
            ) : (
              <>
                <FieldRow label="Verified sender">
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                    {email.sender}
                  </Typography>
                </FieldRow>
                <FieldRow label="Recipients">
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <OverrideChip label="All account emails" />
                    {email.recipients.map((recipient) => (
                      <OverrideChip key={recipient} label={recipient} />
                    ))}
                  </Stack>
                </FieldRow>
              </>
            )}
          </Box>

          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${radius.lg}px`,
              boxShadow: 1,
              py: 3,
              px: 4,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                Events delivered to {isSlack ? 'Slack' : 'email'}
              </Typography>
              <Box
                component="span"
                sx={{
                  ml: 'auto',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'text.secondary',
                  bgcolor: 'background.platform',
                  px: 2,
                  py: 1,
                  borderRadius: `${radius.xxl}px`,
                }}
              >
                {enabledCount} of {allEvents.length} on
              </Box>
            </Stack>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
              Editing <strong>Your defaults · {roleLabel}</strong>. Toggle which events reach{' '}
              {isSlack ? 'Slack' : 'email'} and how often.
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: `${radius.lg}px`,
                overflow: 'hidden',
              }}
            >
              {eventGroups.map((group) => (
                <Box key={group.id}>
                  <Box
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      bgcolor: 'background.page',
                      px: 3,
                      py: 2,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {group.label}
                  </Box>
                  {group.events.map((event, index) => {
                    const pref = prefs[event.id as keyof EventPrefs];
                    const isEnabled = pref[channelId ?? 'slack'];
                    const lastGroup = eventGroups[eventGroups.length - 1];
                    const isLast =
                      index === group.events.length - 1 &&
                      lastGroup !== undefined &&
                      group.id === lastGroup.id;
                    return (
                      <Stack
                        key={event.id}
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          gap: 2,
                          px: 3,
                          py: 2,
                          borderBottom: isLast ? 0 : '1px solid',
                          borderColor: 'divider',
                          bgcolor: isEnabled ? 'transparent' : 'background.page',
                        }}
                      >
                        <Stack sx={{ flex: 1, minWidth: 0, gap: 1 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                            {event.name}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            {event.description}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            flexShrink: 0,
                            opacity: isEnabled ? 1 : 0.4,
                            pointerEvents: isEnabled ? 'auto' : 'none',
                          }}
                        >
                          <CadencePickerInline
                            value={pref.cadence}
                            disabled={!isEnabled || preferencesPending}
                            onChange={(cadence) => onCadenceChange(event.id, cadence)}
                          />
                        </Box>
                        <Switch
                          checked={isEnabled}
                          disabled={preferencesPending}
                          onChange={() => onToggle(event.id, channelId ?? 'slack')}
                          slotProps={{
                            input: { 'aria-label': `${event.name} ${isSlack ? 'Slack' : 'Email'}` },
                          }}
                        />
                      </Stack>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>

          {isSlack && canManage && (
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'error.light',
                borderRadius: `${radius.lg}px`,
                py: 3,
                px: 4,
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>
                Disconnect workspace
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 11, color: 'text.secondary' }}>
                Stop Slack delivery and revoke {providerName}&apos;s stored Slack credential.
              </Typography>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<DeleteOutlineIcon />}
                disabled={disconnectPending}
                onClick={() => setConfirmDisconnect(true)}
                sx={{ mt: 2 }}
              >
                Disconnect Slack
              </Button>
            </Box>
          )}
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1,
            py: 3,
            px: 5,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          {isSlack && (
            <Button
              size="small"
              variant="text"
              startIcon={<SendIcon />}
              disabled={testPending}
              onClick={onTest}
            >
              Send test
            </Button>
          )}
          {notice?.kind === 'error' ? (
            <Alert severity="error">{notice.text}</Alert>
          ) : notice ? (
            <Typography role="status" sx={{ ml: 1, fontSize: 11, color: `${notice.kind}.main` }}>
              {notice.text}
            </Typography>
          ) : null}
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="gradient" startIcon={<CheckIcon />} onClick={onClose}>
            Done
          </Button>
        </Stack>
      </Drawer>

      <Dialog
        open={confirmDisconnect}
        onClose={() => {
          if (!disconnectPending) setConfirmDisconnect(false);
        }}
        aria-labelledby="disconnect-slack-title"
      >
        <DialogTitle id="disconnect-slack-title">Disconnect Slack?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {providerName} will stop posting notifications to {slack.workspace}. You can reconnect
            later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDisconnect(false)}
            disabled={disconnectPending}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={disconnectPending}
            onClick={() => {
              setConfirmDisconnect(false);
              onDisconnect();
            }}
          >
            Disconnect Slack
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
